import React from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils/helpers';
import { SongCard } from '@components/basics/SongCard';
import { DateTimeTooltip } from '@components/main-components/datetime-tooltip';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { ModalType, useModal } from '@context/modal';
import type { FragmentType, PlaylistInput } from '@graphql/reactQuery';
import { getFragment, ReleaseInfoCardFragmentDoc } from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import { isOpenEdition } from '@utils/auction-helpers';
import { SPACE } from '@utils/constants';
import { CollectedBy } from './PurchaseCard/CollectedBy';
import { ReleaseCardActions } from './ReleaseCardActions';
import { useReleaseCardInfo } from './useReleaseCardInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseInfoCard on Release {
    id
    quantityLowerBound
    quantityUpperBound
    supplyCutoffTimestamp
    mintStartTimestamp
    listeningParty
    track {
      id
    }
    ...ReleaseCardActions
    ...ReleasesCardInfo
    ...ReleaseUpdates
    socialProofCollectors(userId: $userId) {
      ...PurchaseCardCollectedByUser
    }
  }
`);

export const getCountdownString = ({ timeStamp }: { timeStamp: number }) => {
  const distance = formatDistanceToNowStrict(timeStamp, { roundingMethod: 'floor' });
  const [number, unit] = distance.split(' ');
  if (!number || !unit) return '';
  return `${number}${unit.includes('month') ? 'mo' : unit.charAt(0)}`;
};

export const SaleCountdown = ({ endTimeMs }: { endTimeMs: number }) => {
  return (
    <DateTimeTooltip
      dateTime={new Date(endTimeMs)}
      className="ml-auto mr-2 mt-3 flex w-fit items-center overflow-hidden rounded-full bg-black/30 px-2 py-1 text-[10px] font-semibold leading-[14px] text-white backdrop-blur-lg sm:text-base-s"
    >
      <div>
        <span>Ends in{SPACE}</span>
        <span>{getCountdownString({ timeStamp: endTimeMs })}</span>
      </div>
    </DateTimeTooltip>
  );
};

/** Helper that decide if we should show Running Info component  */
export const shouldShowRunningSection = ({
  finalQuantity,
  quantityUpperBound,
  quantityLowerBound,
}: {
  finalQuantity: Maybe<number>;
  quantityUpperBound: number;
  quantityLowerBound: number;
}) => {
  const currentMaxQuantity = finalQuantity ?? quantityUpperBound;
  const isOpen = isOpenEdition(currentMaxQuantity);
  // range edition is when lower and upper bounds are different
  // which is also open edition but OE are special range upper bound
  const isRange = quantityLowerBound < currentMaxQuantity && !isOpen;
  const showEditionRunning = (isOpen || isRange) && !finalQuantity;
  return showEditionRunning;
};

/**
 * Helper that decide if we should show ends in countdown
 * We do not show countdown if the listening party has not started
 *
 * Context: https://soundxyz.slack.com/archives/C044HQ6JT6V/p1675447191575299
 */
export const shouldShowEndsInCountdown = ({
  finalQuantity,
  quantityUpperBound,
  quantityLowerBound,
  mintStartTimestamp,
}: {
  finalQuantity: Maybe<number>;
  quantityUpperBound: number;
  quantityLowerBound: number;
  mintStartTimestamp: number;
}) => {
  const showEditionRunning = shouldShowRunningSection({
    finalQuantity,
    quantityUpperBound,
    quantityLowerBound,
  });

  return showEditionRunning && mintStartTimestamp < Date.now();
};

export const ReleaseInfoCard = ({
  data,
  playlist,
  getNextTrackIds,
  hideSocialProof = false,
}: {
  data: FragmentType<ReleaseInfoCardFragmentDoc>;
  playlist?: PlaylistInput;
  getNextTrackIds: () => string[];
  // Hide social proof when there is no space, eg when there's 2 cards in a row on mobile
  hideSocialProof?: boolean;
}) => {
  const { openModal } = useModal();
  const release = getFragment(ReleaseInfoCardFragmentDoc, data);

  const {
    coverImageUrl,
    trackUrl,
    onPlayClick,
    isAudioBuffering: isBufferingAudio,
    playButtonPrefetchRef,
    isActiveTrack,
    isAlbum,
    hasAudioAccess,
    artistName,
    artistWebappPath,
    releaseTitle,
    currentListeningParty,
  } = useReleaseCardInfo({ data: release, playlist });

  const {
    data: { finalQuantity, totalMinted },
  } = useSubscribeRelease({
    release,
  });

  const {
    quantityUpperBound,
    supplyCutoffTimestamp,
    quantityLowerBound,
    mintStartTimestamp,
    socialProofCollectors,
  } = release;

  const showCountdown = shouldShowEndsInCountdown({
    finalQuantity,
    quantityUpperBound,
    quantityLowerBound,
    mintStartTimestamp,
  });

  const onPlayButtonClick = useStableCallback(async () => {
    await onPlayClick();
    setUpNextTrackIds(getNextTrackIds());
  });

  const onMintCountClick = () => {
    openModal(ModalType.RELEASE_MINTS, { body: <ReleaseMintsModal releaseId={release.id} /> });
  };

  return (
    <SongCard
      album={isAlbum}
      isPlaying={isActiveTrack}
      loading={false}
      edition={false}
      expanded={null}
      setExpanded={null}
    >
      <SongCard.Cover
        header={showCountdown && <SaleCountdown endTimeMs={supplyCutoffTimestamp ?? 0} />}
        coverLink={trackUrl}
        image={<SongCard.Cover.Single src={coverImageUrl} />}
        onPlayButtonClick={onPlayButtonClick}
        isBufferingSong={isBufferingAudio}
        disablePlay={!hasAudioAccess}
        playButtonPrefetchRef={playButtonPrefetchRef}
        currentListeningParty={currentListeningParty}
      />
      <SongCard.Info
        header={
          <AccessibleLink absoluteUri={trackUrl} type="external">
            {releaseTitle}
          </AccessibleLink>
        }
        subheader={<AccessibleLink pathnameUri={artistWebappPath}>{artistName}</AccessibleLink>}
        actions={<ReleaseCardActions data={release} />}
      />
      {hideSocialProof ? (
        <div className="text-overflow-ellipsis -mt-2 mb-1 flex flex-shrink-0  overflow-hidden whitespace-nowrap px-3 py-2 text-base-s text-base400 md:-mt-5">
          {totalMinted > 0 ? (
            <button onClick={onMintCountClick}>{totalMinted} minted</button>
          ) : (
            <span>Be the first to mint</span>
          )}
        </div>
      ) : (
        <div className="-mt-2 mb-1 flex h-11 w-full md:-mt-2.5">
          <CollectedBy
            collectedByFrag={socialProofCollectors}
            totalMinted={totalMinted}
            releaseId={release.id}
          />
        </div>
      )}
    </SongCard>
  );
};

export const SongCardSkeleton = () => {
  return (
    <SongCard
      album={false}
      isPlaying={false}
      edition={false}
      expanded={null}
      setExpanded={null}
      loading
    >
      <SongCard.Cover
        image={null}
        onPlayButtonClick={() => {
          return;
        }}
        currentListeningParty={null}
      />
      <SongCard.Info header={null} subheader={null} actions={null} />
      <SongCard.Running collected={0} price="0 ETH" />
    </SongCard>
  );
};
